<template>
  <div class="box" @dragover.prevent @drop.prevent="onDrop">
    <div v-if="!fileUrl" class="upload" @click="open()">
      <iconpark-icon name="plus-circle-fill" class="mb-[12px]" size="32" />
      <a href="javascript:;">{{ $t('dian-ji-huo-tuo-zhuai-wen-jian-dao-ci-chu-shang-chuan') }}</a>
      <div class="tip" v-html="tip"></div>
    </div>
    <template v-else>
      <div class="w-full rounded-[8px] flex-y-center overflow-hidden">
        <ClientOnly>
          <Crop
            ref="cropRef"
            :width="832"
            :height="468"
            :src="fileUrl"
            :ratio="{ h: 200, w: 1920 }"
          />
        </ClientOnly>
      </div>
    </template>
  </div>
  <a-divider :style="{ margin: '0' }"></a-divider>
  <div class="operation">
    <a-button @click="open()">
      {{ $t('zhong-xin-shang-chuan') }}
    </a-button>
    <a-button
      type="primary"
      :disabled="!fileUrl"
      :loading="loading || upLoading"
      @click="handleConfirm"
    >
      {{ $t('que-ding') }}
    </a-button>
  </div>
</template>

<script lang="ts" setup>
/**
 * 个人头像、团队Logo 共用
 */
import Crop from './Crop.vue';
import type { IResponse } from '~/api/interface';
import { MB_ATOM, transSize, useUpload } from '~/hooks/useUpload';
import { base64ConvertFile } from '~/utils';

const { $t } = useNuxtApp();

const cropRef = ref();

export interface IProps {
  url: string; // 数据回显用
  defaultUrl: string; // 默认图片
  tip: string; // 提示文案
  request(path?: string): Promise<IResponse>; // 保存接口
  success?(data: any): void; // 成功回调，参数为资源对象（如果有）
  cancel(): void; // 关闭弹窗
}

const props = withDefaults(defineProps<IProps>(), {
  success() {
    console.log('success');
  },
});

const fileUrl = ref<string>(props.url);

const loading = ref(false);
const accept = ['jpg', 'jpeg', 'png'];

const {
  open,
  onDrop,
  loading: upLoading,
  simpleUpload,
} = useUpload({
  noModal: true,
  accept: `.${accept.join(',.')}`,
  hasMessage: false,
  beforeUpload: file => {
    const suffix = (file.name.split('.').pop() || '').toLowerCase();
    if (!accept.includes(suffix)) {
      message.error($t('qing-shang-chuan-acceptjoin-ge-shi-wen-jian', [accept.join('、')]));
      return false;
    }
    const size = MB_ATOM * 3;
    if (file.size > size) {
      message.error(
        $t('tu-pian-da-xiao-bu-neng-chao-guo-transsizesizemb-dang-qian-transsizefilesizemb', [
          transSize(size),
          transSize(file.size),
        ]),
      );
      return false;
    }
    if (file?.file) {
      getUrl(file?.file);
    }

    return false;
  },
  onSuccess: async (_, res) => {
    loading.value = true;
    try {
      const setRes = await props.request(res.upload_path);
      loading.value = false;
      if (setRes.code !== 0) {
        return;
      }

      message.success($t('she-zhi-cheng-gong'));
      props.success(setRes.data);
      props.cancel();
    } catch (error) {
      console.error(error);

      loading.value = false;
    }
  },
});

const getUrl = async (file: File) => {
  const reader = new FileReader();
  await reader.readAsDataURL(file);
  reader.onload = () => {
    fileUrl.value = reader.result as string;
  };
};

// 提交
const handleConfirm = async () => {
  const data = await cropRef.value.getImageData();
  await simpleUpload({ file: base64ConvertFile(data) });
};
</script>

<style lang="scss" scoped>
.box {
  @apply w-full relative p-[20px];
}

.operation {
  display: flex;
  justify-content: flex-end;
  padding: 16px 20px;
  gap: 10px;
}
.upload {
  @apply mb-[90px] w-full h-[166px] bg-back-2 flex-center select-none flex-col cursor-pointer;
  border: 1px dashed #d9d9e1;
  &:hover {
    @apply bg-[#f3f4ff] border-[#8580FF];
  }
  a {
    @apply text-14 font-semibold;
  }
  .tip {
    @apply text-12 text-font-50 mt-[6px];
  }
}
.upgradation {
  @apply flex-y-center text-14 font-semibold;
}
.vue-cropper.cropper-area {
  background: white;
  border: 1px dashed #d9d9e1;
}
</style>
